import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Marquee from 'react-fast-marquee';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkle, Leaf, Fire } from '@phosphor-icons/react';
import { ProductCard } from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = 'https://i.imgur.com/8iD261r.jpeg';

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        await axios.post(`${API}/seed`);
        const [prodRes, catRes] = await Promise.all([
          axios.get(`${API}/products?featured=true`),
          axios.get(`${API}/categories`)
        ]);
        setFeatured(prodRes.data);
        setCategories(catRes.data);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.error(err);
      }
    };
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubscribe = useCallback(async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success('Welcome to the CandleCraze family!');
      setEmail('');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubscribing(false);
    }
  }, [email]);

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1594970351817-5eb9a5728fa0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxnb2xkJTIwZ2xpdHRlciUyMHNwYXJrbGUlMjBib2tlaCUyMGx1eHVyeSUyMGRhcmt8ZW58MHx8fHwxNzgxOTI2NjgwfDA&ixlib=rb-4.1.0&q=85&w=1920"
            alt="Gold sparkle background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <img src={LOGO_URL} alt="CandleCraze Designs & More" className="w-52 h-52 sm:w-72 sm:h-72 mx-auto object-contain mb-4 rounded-lg" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="font-['Cormorant_Garamond'] text-5xl sm:text-7xl lg:text-8xl font-black text-white tracking-tight leading-[1.05] italic mb-8"
            style={{ textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}
          >
            Effortless Beauty,<br />Sweetly Done
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Button asChild className="bg-[#0A0A0A] hover:bg-[#1E1E1E] text-[#E8D5A3] rounded-full px-8 py-3 text-sm font-bold h-auto" data-testid="hero-shop-btn">
              <Link to="/shop">Shop Collection</Link>
            </Button>
            <Button asChild variant="outline" className="border-[#0A0A0A]/60 text-[#0A0A0A] hover:bg-[#0A0A0A]/10 rounded-full px-8 py-3 text-sm font-medium h-auto bg-transparent" data-testid="hero-about-btn">
              <Link to="/about">Our Story</Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Marquee Ribbon */}
      <div className="cc-marquee-ribbon" data-testid="marquee-ribbon">
        <Marquee speed={40} gradient={false} pauseOnHover>
          <span className="mx-12">Hand-Poured with Love</span>
          <Sparkle size={16} weight="fill" className="inline mx-2 opacity-60" />
          <span className="mx-12">Clean-Burning Soy Blend</span>
          <Sparkle size={16} weight="fill" className="inline mx-2 opacity-60" />
          <span className="mx-12">Eco-Friendly Ingredients</span>
          <Sparkle size={16} weight="fill" className="inline mx-2 opacity-60" />
          <span className="mx-12">Artisan Crafted Scents</span>
          <Sparkle size={16} weight="fill" className="inline mx-2 opacity-60" />
          <span className="mx-12">Effortless Beauty, Sweetly Done</span>
          <Sparkle size={16} weight="fill" className="inline mx-2 opacity-60" />
          <span className="mx-12">Candles, Wax Melts & Skincare</span>
          <Sparkle size={16} weight="fill" className="inline mx-2 opacity-60" />
        </Marquee>
      </div>

      {/* Brand Story Section */}
      <section className="py-20 sm:py-28 px-4 sm:px-8 relative overflow-hidden" data-testid="brand-story-section">
        <div className="absolute inset-0 bg-[#0A0A0A]">
          <div className="absolute inset-0 cc-sparkle-overlay opacity-30" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 items-center">
            {/* Left Side */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-right lg:text-right space-y-6"
            >
              <div>
                <p className="uppercase text-[10px] tracking-[0.3em] text-[#C4A35A] font-medium mb-3">Our Promise</p>
                <h3 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl text-[#E8D5A3] italic leading-snug">100% All Natural<br />Ingredients</h3>
              </div>
              <p className="text-sm text-[#9A8E7A] leading-relaxed">
                No fillers. No chemicals. No shortcuts. Every product is handcrafted with ingredients your skin will thank you for. And the candles you light? Made with clean-burning soy, coconut-apricot, and beeswax blends — so what fills your space is just as pure as what touches your skin.
              </p>
              <div className="flex flex-wrap gap-2 justify-end">
                {['Shea Butter', 'Jojoba Oil', 'Vitamin E', 'Cocoa Butter'].map(ingredient => (
                  <span key={ingredient} className="text-[10px] px-3 py-1 rounded-full border border-[#C4A35A]/20 text-[#C4A35A]">{ingredient}</span>
                ))}
              </div>
            </motion.div>

            {/* Center Logo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex justify-center"
            >
              <div className="relative">
                <div className="absolute -inset-4 rounded-2xl bg-[#C4A35A]/5 blur-xl" />
                <img src={LOGO_URL} alt="CandleCraze Designs & More" className="w-48 h-48 sm:w-56 sm:h-56 object-contain rounded-xl relative cc-gold-glow" />
              </div>
            </motion.div>

            {/* Right Side */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="text-left space-y-6"
            >
              <div>
                <p className="uppercase text-[10px] tracking-[0.3em] text-[#C4A35A] font-medium mb-3">Made With Love</p>
                <h3 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl text-[#E8D5A3] italic leading-snug">Dedicated To<br />Your Beauty</h3>
              </div>
              <p className="text-sm text-[#9A8E7A] leading-relaxed">
                We don't just want you to smell amazing and have soft, glowing skin — we want you to know that what you're putting on your body and breathing into your home is actually good for you too. Pure ingredients, real results, nothing artificial.
              </p>
              <div className="flex flex-wrap gap-2 justify-start">
                {['Moroccan Oil', 'Coconut Oil', 'Glycerin', 'Biotin Oil'].map(ingredient => (
                  <span key={ingredient} className="text-[10px] px-3 py-1 rounded-full border border-[#C4A35A]/20 text-[#C4A35A]">{ingredient}</span>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Bottom Tagline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-center mt-16"
          >
            <p className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl text-[#E8D5A3] italic">
              Effortless Beauty, Sweetly Done
            </p>
            <div className="w-16 h-[1px] bg-[#C4A35A]/30 mx-auto mt-6" />
          </motion.div>
        </div>
      </section>

      {/* Shop By Category */}
      <section className="py-16 sm:py-24 px-4 sm:px-8 max-w-7xl mx-auto" data-testid="categories-section">
        <div className="text-center mb-12">
          <p className="uppercase text-xs tracking-[0.2em] text-[#C4A35A] font-medium mb-2">Browse</p>
          <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl lg:text-4xl text-[#E8D5A3]">Shop by Category</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {categories.map((cat) => (
            <motion.div
              key={cat.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Link
                to={`/shop?category=${cat.id}`}
                className="group block relative aspect-[3/4] overflow-hidden rounded-lg border border-[#2A2520] hover:border-[#C4A35A]/30 transition-all"
                data-testid={`category-${cat.id}`}
              >
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/90 via-[#0A0A0A]/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                  <h3 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl text-[#E8D5A3] font-medium">{cat.name}</h3>
                  <p className="text-xs text-[#9A8E7A] mt-1 hidden sm:block">{cat.description}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 sm:py-24 px-4 sm:px-8 max-w-7xl mx-auto" data-testid="featured-section">
        <div className="bg-[#141414] rounded-2xl border border-[#2A2520] p-8 sm:p-12">
          <div className="text-center mb-12">
            <p className="uppercase text-xs tracking-[0.2em] text-[#C4A35A] font-medium mb-2">Bestsellers</p>
            <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl lg:text-4xl text-[#E8D5A3]">Fan Favorites</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 sm:gap-8">
            {featured.slice(0, 8).map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
          <div className="text-center mt-12">
            <Button asChild className="cc-btn-secondary rounded-full px-8 py-3 h-auto bg-transparent" data-testid="view-all-btn">
              <Link to="/shop">
                View All Products <ArrowRight size={16} className="ml-2 inline" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why CandleCraze */}
      <section className="py-16 sm:py-24 px-4 sm:px-8 max-w-5xl mx-auto" data-testid="why-section">
        <div className="text-center mb-12">
          <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl lg:text-4xl text-[#E8D5A3] mb-4">Why CandleCraze?</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center p-8">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#C4A35A]/10 flex items-center justify-center border border-[#C4A35A]/20">
              <Fire size={24} weight="duotone" className="text-[#C4A35A]" />
            </div>
            <h3 className="font-['Cormorant_Garamond'] text-xl font-medium text-[#E8D5A3] mb-2">Powerful Throw</h3>
            <p className="text-sm text-[#9A8E7A] leading-relaxed">ECO cotton and wooden crackling wicks deliver strong hot and cold throw throughout your space.</p>
          </div>
          <div className="text-center p-8">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#C4A35A]/10 flex items-center justify-center border border-[#C4A35A]/20">
              <Leaf size={24} weight="duotone" className="text-[#C4A35A]" />
            </div>
            <h3 className="font-['Cormorant_Garamond'] text-xl font-medium text-[#E8D5A3] mb-2">Eco-Friendly</h3>
            <p className="text-sm text-[#9A8E7A] leading-relaxed">Clean-burning soy, coconut-apricot, and white beeswax blends. Sustainable beauty across all our products.</p>
          </div>
          <div className="text-center p-8">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#C4A35A]/10 flex items-center justify-center border border-[#C4A35A]/20">
              <Sparkle size={24} weight="duotone" className="text-[#C4A35A]" />
            </div>
            <h3 className="font-['Cormorant_Garamond'] text-xl font-medium text-[#E8D5A3] mb-2">Artisan Crafted</h3>
            <p className="text-sm text-[#9A8E7A] leading-relaxed">Hand-poured in Egg Harbor Township with fruity, floral, fresh, and woody scents. Each product made with care.</p>
          </div>
        </div>
      </section>

      {/* TikTok Section */}
      <section className="relative py-16 sm:py-20 overflow-hidden cc-sparkle-overlay" data-testid="tiktok-section">
        <div className="absolute inset-0 bg-[#141414]" />
        <div className="relative z-10 text-center px-4 max-w-2xl mx-auto">
          <p className="uppercase text-xs tracking-[0.2em] text-[#C4A35A] font-medium mb-2">Follow Us</p>
          <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl lg:text-4xl text-[#E8D5A3] mb-4">Join the CandleCraze on TikTok</h2>
          <p className="text-sm text-[#9A8E7A] mb-8 leading-relaxed">See behind-the-scenes of our hand-pouring process, new scent reveals, and customer favorites.</p>
          <a
            href="https://www.tiktok.com/@cd37220"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#C4A35A] text-[#0A0A0A] rounded-full px-8 py-3 text-sm font-bold hover:bg-[#D4B86A] transition-colors"
            data-testid="tiktok-follow-btn"
          >
            Follow @cd37220 on TikTok
          </a>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-8 max-w-2xl mx-auto text-center" data-testid="newsletter-section">
        <p className="uppercase text-xs tracking-[0.2em] text-[#C4A35A] font-medium mb-2">Stay in the Glow</p>
        <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl lg:text-4xl text-[#E8D5A3] mb-3">Join Our Fragrant Journey</h2>
        <p className="text-sm text-[#9A8E7A] mb-8">Be the first to know about new scents, exclusive deals, and behind-the-scenes peeks.</p>
        <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="newsletter-form">
          <Input
            type="email"
            placeholder="Your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 rounded-full border-[#2A2520] bg-[#141414] text-[#F0E6D0] placeholder:text-[#9A8E7A]/50 focus:border-[#C4A35A] focus:ring-[#C4A35A] h-12 px-6 text-sm"
            data-testid="newsletter-email-input"
          />
          <Button
            type="submit"
            disabled={subscribing}
            className="bg-[#C4A35A] hover:bg-[#D4B86A] text-[#0A0A0A] rounded-full px-8 h-12 text-sm font-bold"
            data-testid="newsletter-submit-btn"
          >
            {subscribing ? 'Signing up...' : 'Sign Up'}
          </Button>
        </form>
      </section>
    </div>
  );
}
